import { Link } from "@inertiajs/react"
import { AppLayout } from "../../components/AppLayout"
import { Badge } from "../../components/Badge"
import { MiniBarChart } from "../../components/MiniBarChart"
import { SectionCard } from "../../components/SectionCard"

type Props = {
  customer_mode?: boolean
  days: number
  card: {
    id: number
    title: string
    slug: string
    public_url: string
    preview_url: string
    edit_url: string
  }
  analytics: {
    visits: number
    unique_visitors: number
    action_clicks: number
    actions: Record<string, number>
    sources: Record<string, number>
    devices: Record<string, number>
    series: Array<{ label: string; value: number }>
  }
}

export default function Analytics({ card, analytics, days, customer_mode = false }: Props) {
  const conversionRate = analytics.visits > 0 ? Math.round((analytics.action_clicks / analytics.visits) * 100) : 0

  return (
    <AppLayout title={`${card.title} | ביצועים`} customerMode={customer_mode}>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <h1 className="text-3xl font-bold">ביצועים</h1>
            <Badge tone="slate">{card.slug}</Badge>
          </div>
          <p className="text-slate-600">{card.title}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[7, 30, 90].map((range) => <Link key={range} href={`${window.location.pathname}?days=${range}`} className={`rounded-xl px-3 py-2 text-sm font-medium ${days === range ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{range} ימים</Link>)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link href={card.edit_url} className="btn-secondary">חזרה לעריכה</Link>
          <Link href={card.preview_url} className="btn-secondary">תצוגה</Link>
          <a href={card.public_url} className="btn-secondary" target="_blank" rel="noreferrer">פתיחת הכרטיס</a>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_0.9fr]">
        <SectionCard title="תנועה אחרונה" description={`${days} ימים אחרונים`}>
          <div className="mb-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500">כניסות</div>
              <div className="mt-1 text-3xl font-semibold">{analytics.visits}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500">אנשים שונים</div>
              <div className="mt-1 text-3xl font-semibold">{analytics.unique_visitors}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500">פעולות</div>
              <div className="mt-1 text-3xl font-semibold">{analytics.action_clicks}</div>
              <div className="mt-1 text-xs text-slate-500">{conversionRate}% מהכניסות</div>
            </div>
          </div>
          <MiniBarChart points={analytics.series} />
        </SectionCard>

        <SectionCard title="פעולות בכרטיס" description={`${days} ימים אחרונים`}>
          <div className="space-y-3">
            {["phone", "whatsapp", "email", "website", "maps", "vcf", "custom"].map((action) => (
              <div key={action} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-sm">
                <span>{actionLabels[action]}</span>
                <span className="font-semibold text-slate-900">{analytics.actions[action] || 0}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <SectionCard title="מקורות הגעה" description="מאיפה הגיעו המבקרים">
          <AnalyticsBreakdown values={analytics.sources} labels={{ direct: "כניסה ישירה" }} />
        </SectionCard>
        <SectionCard title="מכשירים" description="סוג המכשיר של המבקרים">
          <AnalyticsBreakdown values={analytics.devices} labels={{ mobile: "נייד", tablet: "טאבלט", desktop: "מחשב", unknown: "לא ידוע" }} />
        </SectionCard>
      </div>
    </AppLayout>
  )
}

const actionLabels: Record<string, string> = {
  phone: "התקשרויות",
  whatsapp: "וואטסאפ",
  email: "אימייל",
  website: "אתר",
  maps: "ניווט",
  vcf: "שמירת איש קשר",
  custom: "כפתורים נוספים",
}

function AnalyticsBreakdown({ values, labels }: { values: Record<string, number>; labels: Record<string, string> }) {
  const entries = Object.entries(values).map(([key, value]) => [key || "direct", value] as const).sort(([, first], [, second]) => second - first)
  if (entries.length === 0) return <p className="text-sm text-slate-500">עדיין אין מספיק נתונים.</p>

  return <div className="space-y-3">{entries.map(([key, value]) => <div key={key} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-sm"><span>{labels[key] || key}</span><span className="font-semibold">{value}</span></div>)}</div>
}
